class ProductGalleryThumbnails extends HTMLElement {
  constructor() {
    super();

    /**
     * All thumbnail elements in the gallery
     * @type {NodeListOf<HTMLElement>}
     */
    this.thumbnails = this.querySelectorAll('.product-gallery__thumbnail');

    /**
     * Closest wrapper that contains the main media list
     * @type {HTMLElement|null}
     */
    this.mainMediaWrapper = this.closest('.product__media-wrapper');

    /**
     * Currently active media id
     * @type {string|null}
     */
    this.currentMediaId = null;

    /**
     * Media query for mobile-only sliding behavior
     * @type {MediaQueryList}
     */
    this.mobileMql = window.matchMedia('(max-width: 749px)');

    /**
     * Touch/swipe state: starting X position
     * @private
     * @type {number}
     */
    this._touchStartX = 0;

    /**
     * Touch/swipe state: starting Y position
     * @private
     * @type {number}
     */
    this._touchStartY = 0;

    /**
     * Touch/swipe state: horizontal delta
     * @private
     * @type {number}
     */
    this._touchDeltaX = 0;

    /**
     * Touch/swipe state: whether we are actively swiping horizontally
     * @private
     * @type {boolean}
     */
    this._isSwiping = false;

    /**
     * Bound viewport change handler reference (for add/remove listeners safely)
     * @private
     * @type {(e: MediaQueryListEvent) => void}
     */
    this._boundViewportChange = this.onViewportChange.bind(this);
  }

  connectedCallback() {
    this.setupEventListeners();
    this.setupKeyboardNavigation();

    // Initialize current media id from active thumbnail (if present)
    const activeThumb = this.querySelector('.product-gallery__thumbnail--active');
    if (activeThumb) {
      this.currentMediaId = String(activeThumb.dataset.mediaId);
    } else {
      console.warn('ProductGallery: No active thumbnail found on initialization');
    }

    // Mobile-only: enable swipe navigation
    if (this.mobileMql.matches) {
      this.setupSwipeNavigation();
    }

    // Listen for viewport changes to toggle mobile-only behavior safely
    if (typeof this.mobileMql.addEventListener === 'function') {
      this.mobileMql.addEventListener('change', this._boundViewportChange);
    } else if (typeof this.mobileMql.addListener === 'function') {
      // Safari fallback
      this.mobileMql.addListener(this._boundViewportChange);
    }

    this.initializeGallery();
  }

  disconnectedCallback() {
    // Clean up the media query listener if supported
    if (this.mobileMql) {
      if (typeof this.mobileMql.removeEventListener === 'function') {
        this.mobileMql.removeEventListener('change', this._boundViewportChange);
      } else if (typeof this.mobileMql.removeListener === 'function') {
        // Safari fallback
        this.mobileMql.removeListener(this._boundViewportChange);
      }
    }
  }

  /**
   * Handle viewport changes between mobile and desktop
   * Ensures transforms are reset when leaving mobile and re-synced when entering
   * @param {MediaQueryListEvent} event
   */
  onViewportChange(event) {
    const mediaList = this.getMediaList();
    if (!mediaList) return;

    if (!event.matches) {
      // Leaving mobile: reset sliding styles
      mediaList.style.transform = '';
      mediaList.classList.remove('is-dragging');
    } else {
      // Entering mobile: attach swipe listeners (idempotent) and sync slide
      this.setupSwipeNavigation();

      const items = Array.from(mediaList.querySelectorAll('.product-gallery__media-item'));
      const index = this.getMediaIndex(items, this.currentMediaId);
      if (index >= 0) this.slideToIndex(index);
    }
  }

  /**
   * Set up event listeners for thumbnails and variant changes
   */
  setupEventListeners() {
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', this.handleThumbnailClick.bind(this));
    });

    // Update gallery when product variant changes
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event.data?.variant;
        if (variant?.featured_media) {
          const mediaId = String(variant.featured_media.id);

          // Update thumbnail state
          this.setActiveMedia(mediaId);

          // Slide + sync main media
          const position =
            this.querySelector(`.product-gallery__thumbnail[data-media-id="${mediaId}"]`)?.dataset?.mediaPosition ||
            '1';

          this.updateMainMedia(mediaId, String(position));
        }
      });
    }
  }

  /**
   * Handle thumbnail click events
   * @param {Event} event - Click event
   */
  handleThumbnailClick(event) {
    event.preventDefault();

    /** @type {HTMLElement} */
    const thumbnail = event.currentTarget;
    const mediaId = String(thumbnail.dataset.mediaId);
    const position = String(thumbnail.dataset.mediaPosition || '');

    if (!mediaId) return;
    if (mediaId === this.currentMediaId) return;

    this.setActiveMedia(mediaId);
    this.updateMainMedia(mediaId, position);
  }

  /**
   * Initialize the gallery state and preload videos
   * Sets initial slide position based on active thumbnail or first item
   */
  initializeGallery() {
    const mediaList = this.getMediaList();
    if (!mediaList) return;

    const allMediaItems = Array.from(mediaList.querySelectorAll('.product-gallery__media-item'));
    if (!allMediaItems.length) return;

    // Preload videos to prevent poster flash
    allMediaItems.forEach((item) => this.preloadVideos(item));

    // Determine initial active index
    let activeIndex = 0;
    if (this.currentMediaId) {
      const idx = this.getMediaIndex(allMediaItems, this.currentMediaId);
      if (idx >= 0) activeIndex = idx;
    } else {
      // If no active thumb was marked, default to first item id for consistency
      this.currentMediaId = String(allMediaItems[0].dataset.mediaId || '');
      const firstThumb = this.querySelector(`.product-gallery__thumbnail[data-media-id="${this.currentMediaId}"]`);
      if (firstThumb) this.setActiveMedia(this.currentMediaId);
    }

    // Apply active class
    allMediaItems.forEach((item, index) => {
      item.classList.toggle('product-gallery__media-item--active', index === activeIndex);
    });

    // Mobile-only: position the track
    if (this.mobileMql.matches) {
      this.slideToIndex(activeIndex);
    } else {
      // Desktop/tablet: clear transform so layout remains as your CSS dictates
      mediaList.style.transform = '';
    }

    // Ensure lazy load/deferred media of active slide is initialized
    const activeItem = allMediaItems[activeIndex];
    if (activeItem) {
      this.triggerLazyLoad(activeItem);
      this.resumeVideosInItem(activeItem);
    }
  }

  /**
   * Update the main product media display and (on mobile) slide track
   * @param {string} mediaId - Media ID to display
   * @param {string} position - Media position
   */
  updateMainMedia(mediaId, position) {
    const mediaList = this.getMediaList();
    if (!mediaList) return;

    const allMediaItems = this.getAllMediaItems(mediaList);
    if (!allMediaItems.length) return;

    // Deactivate current active state
    this.deactivateCurrentMedia(allMediaItems);

    // Activate new target
    const activeEl = this.activateTargetMedia(mediaList, mediaId, allMediaItems);

    // Mobile-only: slide track to the new index
    if (this.mobileMql.matches) {
      const itemsArray = Array.from(allMediaItems);
      const targetIndex = this.getMediaIndex(itemsArray, mediaId);
      if (targetIndex >= 0) this.slideToIndex(targetIndex);
    }

    // Announce / bubble event
    this.dispatchMediaChangeEvent(mediaId, position);

    // Keep currentMediaId aligned to what is actually active
    if (activeEl?.dataset?.mediaId) {
      this.currentMediaId = String(activeEl.dataset.mediaId);
    } else {
      this.currentMediaId = String(mediaId);
    }
  }

  /**
   * Get the media list container (the sliding track on mobile)
   * @returns {HTMLElement|null} Media list element
   */
  getMediaList() {
    const mediaList = this.mainMediaWrapper?.querySelector('.product__media-list');
    if (!mediaList) console.warn('ProductGallery: Media list not found');
    return mediaList;
  }

  /**
   * Get all media items from the media list
   * @param {HTMLElement} mediaList - Media list container
   * @returns {NodeListOf<HTMLElement>} All media items
   */
  getAllMediaItems(mediaList) {
    const allMediaItems = mediaList.querySelectorAll('.product-gallery__media-item');
    if (allMediaItems.length === 0) console.warn('ProductGallery: No media items found');
    return allMediaItems;
  }

  /**
   * Get the index of a media item by mediaId
   * @param {HTMLElement[]} items - Media items
   * @param {string|null} mediaId - Media ID to find
   * @returns {number} Index of the media item, or -1 if not found
   */
  getMediaIndex(items, mediaId) {
    if (!mediaId) return -1;
    return items.findIndex((item) => String(item.dataset.mediaId) === String(mediaId));
  }

  /**
   * Slide the track to a specific index (mobile only)
   * @param {number} index - Slide index
   */
  slideToIndex(index) {
    if (!this.mobileMql.matches) return;

    const mediaList = this.getMediaList();
    if (!mediaList) return;

    mediaList.style.transform = `translateX(-${index * 100}%)`;
  }

  /**
   * Deactivate current media and pause videos
   * @param {NodeListOf<HTMLElement>|HTMLElement[]} allMediaItems - All media items
   */
  deactivateCurrentMedia(allMediaItems) {
    allMediaItems.forEach((item) => {
      if (item.classList.contains('product-gallery__media-item--active')) {
        this.pauseVideosInItem(item);
      }
      item.classList.remove('product-gallery__media-item--active');
    });
  }

  /**
   * Activate target media or fallback to first item
   * @param {HTMLElement} mediaList - Media list container
   * @param {string} mediaId - Target media ID
   * @param {NodeListOf<HTMLElement>} allMediaItems - All media items
   * @returns {HTMLElement|null} Activated media element
   */
  activateTargetMedia(mediaList, mediaId, allMediaItems) {
    const targetMedia = mediaList.querySelector(`.product-gallery__media-item[data-media-id="${mediaId}"]`);
    if (targetMedia) return this.activateMediaItem(targetMedia);
    return this.activateFallbackMedia(allMediaItems, mediaId);
  }

  /**
   * Activate a specific media item
   * @param {HTMLElement} mediaItem - Media item to activate
   * @returns {HTMLElement} The activated media item
   */
  activateMediaItem(mediaItem) {
    mediaItem.classList.add('product-gallery__media-item--active');
    this.triggerLazyLoad(mediaItem);
    this.resumeVideosInItem(mediaItem);
    return mediaItem;
  }

  /**
   * Activate fallback media (first item) when target not found
   * @param {NodeListOf<HTMLElement>} allMediaItems - All media items
   * @param {string} mediaId - Original target media ID
   * @returns {HTMLElement|null} Activated fallback media
   */
  activateFallbackMedia(allMediaItems, mediaId) {
    if (allMediaItems.length > 0) {
      const fallbackMedia = allMediaItems[0];
      this.activateMediaItem(fallbackMedia);
      console.warn(`ProductGallery: Target media ${mediaId} not found, showing first media`);
      return fallbackMedia;
    }
    return null;
  }

  /**
   * Pause all videos in a media item
   * @param {HTMLElement} mediaItem - Media item containing videos
   */
  pauseVideosInItem(mediaItem) {
    const videos = mediaItem.querySelectorAll('video');
    videos.forEach((video) => {
      if (!video.paused) video.pause();
    });
  }

  /**
   * Resume videos in a media item if appropriate
   * Only resumes for video-type media containers
   * @param {HTMLElement} mediaItem - Media item containing videos
   */
  resumeVideosInItem(mediaItem) {
    const isVideo = mediaItem.querySelector('.product-media-container')?.classList.contains('media-type-video');
    if (!isVideo) return;

    const videos = mediaItem.querySelectorAll('video');
    videos.forEach((video) => {
      if (video.paused && video.currentTime > 0) {
        video.play().catch(() => {
          // Autoplay restrictions are expected on some devices
        });
      }
    });
  }

  /**
   * Dispatch media change event for other components
   * @param {string} mediaId - Media ID
   * @param {string} position - Media position
   */
  dispatchMediaChangeEvent(mediaId, position) {
    this.dispatchEvent(
      new CustomEvent('gallery:media-changed', {
        bubbles: true,
        detail: { mediaId, position },
      })
    );
  }

  /**
   * Trigger lazy loading for images and deferred media in the active slide
   * @param {HTMLElement} mediaContainer - Media container element
   */
  triggerLazyLoad(mediaContainer) {
    const allImages = mediaContainer.querySelectorAll('img');
    allImages.forEach((img) => {
      const originalSrc = img.src;
      if (originalSrc) {
        img.src = '';
        img.src = originalSrc;
      }
    });

    const deferredMedia = mediaContainer.querySelectorAll('deferred-media, product-model');
    deferredMedia.forEach((deferred) => {
      if (!deferred.hasAttribute('loaded')) {
        if (typeof deferred.loadContent === 'function') {
          try {
            deferred.loadContent();
          } catch {
            // ignore
          }
        }

        const posterButton = deferred.querySelector('.deferred-media__poster');
        if (posterButton) posterButton.click();
      } else {
        const videos = deferred.querySelectorAll('video');
        videos.forEach((video) => {
          if (video.paused) {
            video.play().catch(() => {
              // ignore
            });
          }
        });
      }
    });

    mediaContainer.offsetHeight;
  }

  /**
   * Set the active thumbnail and update UI state
   * @param {string} mediaId - Media ID to set as active
   */
  setActiveMedia(mediaId) {
    this.currentMediaId = String(mediaId);

    this.thumbnails.forEach((thumbnail) => {
      const isActive = String(thumbnail.dataset.mediaId) === String(mediaId);
      thumbnail.classList.toggle('product-gallery__thumbnail--active', isActive);
      thumbnail.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    const activeThumbnail = this.querySelector(`.product-gallery__thumbnail[data-media-id="${mediaId}"]`);
    if (activeThumbnail) this.scrollToThumbnail(activeThumbnail);
  }

  /**
   * Scroll to ensure thumbnail is visible in the container
   * @param {HTMLElement} thumbnail - Thumbnail element to scroll to
   */
  scrollToThumbnail(thumbnail) {
    const wrapper = this.querySelector('.product-gallery__thumbnails-wrapper');
    if (!wrapper) return;

    const isMobile = window.innerWidth < 750;

    if (isMobile) {
      const thumbnailLeft = thumbnail.offsetLeft;
      const thumbnailWidth = thumbnail.offsetWidth;
      const wrapperWidth = wrapper.offsetWidth;
      const scrollLeft = wrapper.scrollLeft;

      if (thumbnailLeft < scrollLeft) {
        wrapper.scrollLeft = thumbnailLeft;
      } else if (thumbnailLeft + thumbnailWidth > scrollLeft + wrapperWidth) {
        wrapper.scrollLeft = thumbnailLeft + thumbnailWidth - wrapperWidth;
      }
    } else {
      const thumbnailTop = thumbnail.offsetTop;
      const thumbnailHeight = thumbnail.offsetHeight;
      const wrapperHeight = wrapper.offsetHeight;
      const scrollTop = wrapper.scrollTop;

      if (thumbnailTop < scrollTop) {
        wrapper.scrollTop = thumbnailTop;
      } else if (thumbnailTop + thumbnailHeight > scrollTop + wrapperHeight) {
        wrapper.scrollTop = thumbnailTop + thumbnailHeight - wrapperHeight;
      }
    }
  }

  /**
   * Set up keyboard navigation for thumbnails
   */
  setupKeyboardNavigation() {
    this.addEventListener('keydown', (event) => {
      const currentIndex = Array.from(this.thumbnails).findIndex(
        (thumb) => String(thumb.dataset.mediaId) === String(this.currentMediaId)
      );

      let newIndex;
      const isMobile = window.innerWidth < 750;

      switch (event.key) {
        case 'ArrowLeft':
          if (isMobile) newIndex = Math.max(0, currentIndex - 1);
          break;
        case 'ArrowRight':
          if (isMobile) newIndex = Math.min(this.thumbnails.length - 1, currentIndex + 1);
          break;
        case 'ArrowUp':
          if (!isMobile) {
            event.preventDefault();
            newIndex = Math.max(0, currentIndex - 1);
          }
          break;
        case 'ArrowDown':
          if (!isMobile) {
            event.preventDefault();
            newIndex = Math.min(this.thumbnails.length - 1, currentIndex + 1);
          }
          break;
        case 'Home':
          event.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          newIndex = this.thumbnails.length - 1;
          break;
        default:
          return;
      }

      if (newIndex !== undefined && newIndex !== currentIndex && this.thumbnails[newIndex]) {
        this.thumbnails[newIndex].click();
        this.thumbnails[newIndex].focus();
      }
    });
  }

  /**
   * Set up touch swipe navigation for the main media track (mobile only)
   * Adds touch listeners on .product__media-list
   */
  setupSwipeNavigation() {
    if (!this.mobileMql.matches) return;

    const mediaList = this.getMediaList();
    if (!mediaList) return;

    // Prevent duplicate bindings if setupSwipeNavigation is called multiple times
    if (mediaList.dataset.swipeBound === 'true') return;
    mediaList.dataset.swipeBound = 'true';

    mediaList.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
    mediaList.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    mediaList.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
  }

  /**
   * Handle touch start event (mobile only)
   * Records initial touch position for swipe detection
   * @param {TouchEvent} event
   */
  onTouchStart(event) {
    if (!this.mobileMql.matches) return;
    if (!event.touches || event.touches.length !== 1) return;

    const touch = event.touches[0];
    this._touchStartX = touch.clientX;
    this._touchStartY = touch.clientY;
    this._touchDeltaX = 0;
    this._isSwiping = false;

    const mediaList = this.getMediaList();
    if (mediaList) mediaList.classList.add('is-dragging');
  }

  /**
   * Handle touch move event (mobile only)
   * Determines whether user is performing a horizontal swipe
   * Prevents vertical scrolling during horizontal swipe
   * @param {TouchEvent} event
   */
  onTouchMove(event) {
    if (!this.mobileMql.matches) return;
    if (!event.touches || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this._touchStartX;
    const deltaY = touch.clientY - this._touchStartY;

    if (!this._isSwiping) {
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        this._isSwiping = true;
      } else {
        return;
      }
    }

    this._touchDeltaX = deltaX;
    event.preventDefault();
  }

  /**
   * Handle touch end event (mobile only)
   * Determines swipe direction and navigates to adjacent media
   */
  onTouchEnd() {
    if (!this.mobileMql.matches) return;

    const mediaList = this.getMediaList();
    if (mediaList) mediaList.classList.remove('is-dragging');

    if (!this._isSwiping) return;

    const SWIPE_THRESHOLD = 50;
    if (Math.abs(this._touchDeltaX) < SWIPE_THRESHOLD) return;

    if (this._touchDeltaX < 0) {
      this.goToAdjacentMedia(1);
    } else {
      this.goToAdjacentMedia(-1);
    }
  }

  /**
   * Navigate to adjacent media item based on swipe direction (mobile only)
   * @param {number} direction - 1 for next media, -1 for previous media
   */
  goToAdjacentMedia(direction) {
    if (!this.mobileMql.matches) return;

    const mediaList = this.getMediaList();
    if (!mediaList) return;

    const items = Array.from(mediaList.querySelectorAll('.product-gallery__media-item'));
    if (items.length < 2) return;

    let currentIndex = items.findIndex((el) => el.classList.contains('product-gallery__media-item--active'));
    if (currentIndex === -1 && this.currentMediaId) {
      currentIndex = this.getMediaIndex(items, this.currentMediaId);
    }
    if (currentIndex === -1) currentIndex = 0;

    const nextIndex = Math.max(0, Math.min(items.length - 1, currentIndex + direction));
    if (nextIndex === currentIndex) return;

    const nextItem = items[nextIndex];
    const nextMediaId = String(nextItem.dataset.mediaId);

    const thumb = this.querySelector(`.product-gallery__thumbnail[data-media-id="${nextMediaId}"]`);
    const position = String(thumb?.dataset?.mediaPosition || nextIndex + 1);

    this.setActiveMedia(nextMediaId);
    this.updateMainMedia(nextMediaId, position);
  }

  /**
   * Preload videos and hide posters to prevent flash
   * @param {HTMLElement} mediaItem - Media item containing videos
   */
  preloadVideos(mediaItem) {
    const deferredMedia = mediaItem.querySelectorAll('deferred-media');
    deferredMedia.forEach((deferred) => {
      const poster = deferred.querySelector('.deferred-media__poster');
      if (poster) poster.style.display = 'none';

      if (typeof deferred.loadContent === 'function') {
        deferred.loadContent();
      } else if (poster) {
        poster.click();
        poster.style.display = 'none';
      }
    });
  }

  /**
   * Update URL with media parameter for deep linking
   * @param {string} mediaId - Media ID to add to URL
   */
  updateURL(mediaId) {
    if (!mediaId || !window.history.replaceState) return;

    const url = new URL(window.location);
    url.searchParams.set('media', mediaId);
    window.history.replaceState({}, '', url);
  }
}

if (!customElements.get('product-gallery-thumbnails')) {
  customElements.define('product-gallery-thumbnails', ProductGalleryThumbnails);
}